/**
 * Data Provider
 *
 * Translates the React Admin CRUD contract to the existing QR Phone API.
 * See docs/api-notes.md for the API conventions this adapts to:
 *
 * - Envelope { status, data }, lists as { count, rows }.
 * - Pagination/sort/search via page, limit, search, sortwith, sortby.
 * - Exact-match filters via filterKey/filterValue (comma-separated);
 *   a few params (role, client_id, ...) are passed straight through.
 * - snake_case, resource-specific id fields (user_id, qrcode_id, ...).
 * - Empty lists arrive as { status: 'error', data: [] } with HTTP 200.
 * - Organizations and staff are users (role 'organization' / 'staff').
 */

import { DataProvider, RaRecord } from 'react-admin';
import { httpClient, HttpError } from '@/utils/httpClient';
import type {
  ApiEnvelope,
  ApiListData,
  ReactAdminSort,
  ReactAdminPagination,
  ReactAdminFilter,
} from '@/types/api';

// ============================================
// RESOURCE CONFIGURATION
// ============================================

interface ResourceConfig {
  /** API path under /api/v1 */
  endpoint: string;
  /** Primary key field in the API */
  idField: string;
  /** Extra query params always sent on getList */
  listParams?: Record<string, string | number>;
  /** Filter keys passed as direct query params instead of filterKey/filterValue */
  directFilterParams?: string[];
  /** Create uses a different path */
  createPath?: string;
  /** Send create/update as multipart/form-data (user endpoints use multer) */
  multipart?: boolean;
  /** Fields forced into every create payload */
  createDefaults?: Record<string, string | number>;
  /** Query string appended to DELETE (users need ?role_id=<n>) */
  deleteQuery?: (previousData: RaRecord | undefined) => string;
  /** The API has no GET /:id for this resource; look it up via the list */
  getOneViaList?: boolean;
  /** Normalize a record after reading it from the API */
  normalizeRecord?: (record: Record<string, any>) => Record<string, any>;
  /** The endpoint returns the full set: filter/sort/paginate client-side */
  clientSideList?: boolean;
  /** Fields the API needs on every update even when unchanged */
  alwaysSendOnUpdate?: string[];
}

const RESOURCES: Record<string, ResourceConfig> = {
  organizations: {
    endpoint: '/users',
    idField: 'user_id',
    listParams: { role: 2 },
    directFilterParams: ['role', 'client_id'],
    createPath: '/users/create',
    multipart: true,
    createDefaults: { role_id: 2 },
    deleteQuery: () => 'role_id=2',
  },
  staff: {
    endpoint: '/users',
    idField: 'user_id',
    listParams: { role: 3 },
    directFilterParams: ['role', 'client_id', 'organization_id'],
    createPath: '/users/create',
    multipart: true,
    createDefaults: { role_id: 3 },
    deleteQuery: () => 'role_id=3',
  },
  clients: {
    endpoint: '/users',
    idField: 'user_id',
    listParams: { role: 4 },
    directFilterParams: ['role'],
    createPath: '/users/create',
    multipart: true,
    createDefaults: { role_id: 4, status: 1 },
    deleteQuery: () => 'role_id=4',
  },
  admins: {
    endpoint: '/users',
    idField: 'user_id',
    listParams: { role: 1 },
    directFilterParams: ['role'],
    createPath: '/users/create',
    multipart: true,
    createDefaults: { role_id: 1 },
    deleteQuery: () => 'role_id=1',
  },
  users: {
    endpoint: '/users',
    idField: 'user_id',
    directFilterParams: ['role', 'client_id'],
    createPath: '/users/create',
    multipart: true,
    deleteQuery: (prev) => `role_id=${prev?.role_id ?? ''}`,
  },
  departments: {
    endpoint: '/departments',
    idField: 'department_id',
    getOneViaList: true,
    // weekdays is stored as a JSON string ("[0,1,2]")
    normalizeRecord: (record) => {
      if (typeof record.weekdays === 'string') {
        try {
          return { ...record, weekdays: JSON.parse(record.weekdays) };
        } catch {
          return { ...record, weekdays: [] };
        }
      }
      return record;
    },
  },
  qrcodes: {
    endpoint: '/qrcodes',
    idField: 'qrcode_id',
    directFilterParams: ['clientId', 'kioskId', 'status', 'type'],
    getOneViaList: true,
    // expose a form-friendly flag for QRs that never expire
    normalizeRecord: (record) => ({
      ...record,
      no_expiry: !record.qr_end_time || record.qr_end_time === 'never',
    }),
  },
  callDetails: {
    endpoint: '/callDetails',
    idField: 'call_detail_id',
    getOneViaList: true,
  },
  plans: {
    endpoint: '/plans',
    idField: 'plan_id',
    // The payment-gateway sync reads these from the body on every update
    alwaysSendOnUpdate: ['stripe_id', 'stripe_price_id', 'name', 'price', 'duration'],
  },
  modules: {
    endpoint: '/modules',
    idField: 'module_id',
  },
  transactions: {
    endpoint: '/userPlans/transactions',
    idField: 'transaction_id',
    clientSideList: true,
  },
  qrUserDetails: {
    endpoint: '/qrUserDetails',
    idField: 'qr_user_detail_id',
    getOneViaList: true,
  },
  userGroupOrganization: {
    // Many-to-many staff <-> schedule (department/group) mapping within an organization.
    // The list endpoint returns rows with flattened included names
    // ('organization.full_name', 'staff.full_name', 'department.name').
    endpoint: '/userGroupOrganization',
    idField: 'user_group_organization_id',
    listParams: { role: 3 },
    directFilterParams: ['role', 'organization_id'],
    getOneViaList: true,
  },
  connections: {
    // Read-only login/logout audit. The backend scopes the rows to the
    // caller's organization(s) (roles 4/5 via user_organizations); admin sees
    // all. Returns the standard { count, rows } envelope with server-side
    // pagination/sort, so no client-side strategy is needed.
    endpoint: '/connections/details',
    idField: 'connection_details_id',
    getOneViaList: true,
  },
  signupRequests: {
    // Sign-up requests left by visitors when the instance does not charge
    // online. Administrator-only and NOT tenant-scoped: a prospective client
    // belongs to no tenant yet, so there is nothing to scope by.
    // The endpoint paginates by limit/offset, returns the rows directly in
    // `data` with the count at the envelope root and always orders
    // newest-first, so getList uses a dedicated strategy below. There is no
    // GET /:id, hence no `edit`/`show` view for this resource.
    endpoint: '/signupRequests',
    idField: 'signup_request_id',
  },
  orgManagers: {
    // Organization managers are users with role 5. Reuses the /users endpoint
    // (role-scoped view) exactly like clients/admins; the backend scopes the
    // list to the caller's owned organizations.
    endpoint: '/users',
    idField: 'user_id',
    listParams: { role: 5 },
    directFilterParams: ['role'],
    createPath: '/users/create',
    multipart: true,
    createDefaults: { role_id: 5 },
    deleteQuery: () => 'role_id=5',
  },
};

const getConfig = (resource: string): ResourceConfig => {
  const config = RESOURCES[resource];
  if (!config) {
    throw new Error(`Recurso no configurado: ${resource}`);
  }
  return config;
};

// ============================================
// HELPERS
// ============================================

/** Fields never sent back on update (server-managed or denormalized) */
const READONLY_FIELDS = new Set([
  'id',
  'createdAt',
  'updatedAt',
  'created_at',
  'updated_at',
  'login_token',
  'last_login',
  'reset_otp',
  'reset_otp_created',
  'qr_auth_img',
  'device_id',
  'device_os',
  'ios_fcm_token',
  'is_deleted',
  'user_plan_id',
  'plan_status',
  'role.role',
  'user.full_name',
  'plan.name',
  'organizations_array',
  'deptArr',
  'staffArr',
  'default_staff_name',
  'qr_img',
  'calllink_id',
]);

const ensureId = <T extends Record<string, any>>(
  record: T,
  config: ResourceConfig
): T & { id: any } => {
  const normalized = config.normalizeRecord ? config.normalizeRecord(record) : record;
  return { ...normalized, id: normalized[config.idField] } as T & { id: any };
};

/**
 * Map React Admin list params to the API query string
 */
const buildListQuery = (
  config: ResourceConfig,
  pagination?: ReactAdminPagination,
  sort?: ReactAdminSort,
  filter?: ReactAdminFilter
): string => {
  const params = new URLSearchParams();

  if (pagination) {
    params.set('page', String(pagination.page));
    params.set('limit', String(pagination.perPage));
  }

  if (sort?.field && sort.field !== 'id') {
    params.set('sortwith', sort.field);
    params.set('sortby', sort.order === 'DESC' ? '1' : '0');
  }

  Object.entries(config.listParams ?? {}).forEach(([key, value]) => {
    params.set(key, String(value));
  });

  const filterKeys: string[] = [];
  const filterValues: string[] = [];

  Object.entries(filter ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (key === 'q') {
      params.set('search', String(value));
    } else if (config.directFilterParams?.includes(key)) {
      params.set(key, String(value));
    } else {
      filterKeys.push(key);
      // The API splits filterValue on commas; a stray comma would shift filters
      filterValues.push(String(value).replace(/,/g, ' '));
    }
  });

  if (filterKeys.length > 0) {
    params.set('filterKey', filterKeys.join(','));
    params.set('filterValue', filterValues.join(','));
  }

  return params.toString();
};

/**
 * Normalize the three list payload shapes the API produces:
 * { count, rows }, a plain array, or { status: 'error', data: [] } for
 * empty result sets (HTTP 200).
 */
const parseListPayload = (
  json: ApiEnvelope<ApiListData | Record<string, any>[]>
): { rows: Record<string, any>[]; total: number } => {
  if (json.status === 'error') {
    return { rows: [], total: 0 };
  }

  const payload = json.data;

  if (Array.isArray(payload)) {
    return { rows: payload, total: payload.length };
  }

  if (payload && Array.isArray((payload as ApiListData).rows)) {
    const list = payload as ApiListData;
    return {
      rows: list.rows,
      total: list.count ?? list.cound ?? list.rows.length,
    };
  }

  return { rows: [], total: 0 };
};

/**
 * Build the write payload: strip read-only fields, then either JSON or
 * multipart FormData (user endpoints parse multipart via multer).
 * React Admin file inputs provide { rawFile } objects.
 */
const buildWriteBody = (
  config: ResourceConfig,
  data: Record<string, any>,
  defaults?: Record<string, string | number>
): FormData | Record<string, any> => {
  const merged: Record<string, any> = { ...data, ...(defaults ?? {}) };
  Object.keys(merged).forEach((key) => {
    if (READONLY_FIELDS.has(key) || merged[key] === undefined) {
      delete merged[key];
    }
  });
  delete merged[config.idField];

  if (!config.multipart) {
    return merged;
  }

  const form = new FormData();
  Object.entries(merged).forEach(([key, value]) => {
    if (value === null) return;
    if (value && typeof value === 'object' && 'rawFile' in value) {
      form.append(key, (value as { rawFile: File }).rawFile);
    } else if (Array.isArray(value) || typeof value === 'object') {
      form.append(key, JSON.stringify(value));
    } else {
      form.append(key, String(value));
    }
  });
  return form;
};

const fetchOneViaList = async (
  resource: string,
  id: string | number
): Promise<Record<string, any>> => {
  const config = getConfig(resource);
  const query = `filterKey=${config.idField}&filterValue=${encodeURIComponent(String(id))}&page=1&limit=1`;
  const extra = Object.entries(config.listParams ?? {})
    .map(([k, v]) => `&${k}=${v}`)
    .join('');
  const response = await httpClient.get<ApiEnvelope<ApiListData>>(
    `${config.endpoint}?${query}${extra}`
  );
  const { rows } = parseListPayload(response.data);
  if (!rows.length) {
    throw new Error('No se ha encontrado el elemento solicitado');
  }
  return rows[0];
};

// ============================================
// CALL HISTORY LIST STRATEGY
// ============================================

/**
 * GET /callDetails cannot be consumed with plain server-side list params:
 * the server neutralizes `search`, applies sorting in JS per page and has
 * no date-range support (filterKey is exact-match only). Strategy:
 * fetch a window of rows (server-side exact filters + SQL-level sort),
 * then apply date range, free text and final sort client-side.
 * Documented in docs/api-notes.md.
 */
const CALL_DETAILS_WINDOW = 1000;
const CALL_DETAILS_SERVER_FILTERS = [
  'organization_id',
  'user_id',
  'qrcode_id',
  'kiosk_id',
  'callstatus',
  'isCallConnected',
  'booking_id',
];

const getCallDetailsList = async (params: {
  pagination?: ReactAdminPagination;
  sort?: ReactAdminSort;
  filter?: ReactAdminFilter;
}) => {
  const config = RESOURCES.callDetails;
  const filter = params.filter ?? {};

  const serverFilter: ReactAdminFilter = {};
  Object.entries(filter).forEach(([key, value]) => {
    if (CALL_DETAILS_SERVER_FILTERS.includes(key)) {
      serverFilter[key] = value;
    }
  });

  const query = buildListQuery(
    config,
    { page: 1, perPage: CALL_DETAILS_WINDOW },
    { field: 'start_time', order: 'DESC' },
    serverFilter
  );

  let rows: Record<string, any>[] = [];
  let serverTotal = 0;
  try {
    const response = await httpClient.get<ApiEnvelope<ApiListData>>(
      `${config.endpoint}?${query}`
    );
    const parsed = parseListPayload(response.data);
    rows = parsed.rows;
    serverTotal = parsed.total;
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      return { data: [], total: 0 };
    }
    throw error;
  }

  if (serverTotal > CALL_DETAILS_WINDOW) {
    console.warn(
      `callDetails: ${serverTotal} rows match server-side but only the most recent ${CALL_DETAILS_WINDOW} are browsable client-side`
    );
  }

  // Date range on start_time. DateInput provides 'YYYY-MM-DD'; rows carry
  // ISO strings, so date-only lexicographic comparison is timezone-safe.
  const dateFrom = typeof filter.date_from === 'string' ? filter.date_from.slice(0, 10) : null;
  const dateTo = typeof filter.date_to === 'string' ? filter.date_to.slice(0, 10) : null;
  if (dateFrom) {
    rows = rows.filter((row) => String(row.start_time ?? '').slice(0, 10) >= dateFrom);
  }
  if (dateTo) {
    rows = rows.filter((row) => String(row.start_time ?? '').slice(0, 10) <= dateTo);
  }

  if (filter.q) {
    const q = String(filter.q).toLowerCase();
    rows = rows.filter((row) =>
      [row.caller, row.user_name, row.user_email, row.booking_id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }

  const field = params.sort?.field || 'start_time';
  const desc = (params.sort?.order || 'DESC') === 'DESC' ? -1 : 1;
  rows.sort((a, b) => {
    const av = a[field] ?? '';
    const bv = b[field] ?? '';
    if (av < bv) return -1 * desc;
    if (av > bv) return 1 * desc;
    return 0;
  });

  const total = rows.length;
  const page = params.pagination?.page ?? 1;
  const perPage = params.pagination?.perPage ?? 25;
  const slice = rows.slice((page - 1) * perPage, page * perPage);

  return {
    data: slice.map((row) => ensureId(row, config)) as any,
    total,
  };
};

// ============================================
// SIGN-UP REQUEST LIST STRATEGY
// ============================================

/** The tray endpoint caps a page at this many rows. */
const SIGNUP_REQUESTS_MAX_PAGE = 200;

/**
 * GET /signupRequests does not follow the shared list conventions: it takes
 * limit/offset instead of page/limit, answers { data: [...], total } instead
 * of { data: { count, rows } }, exposes `state` as a direct query param and
 * always orders newest-first (no sortwith). Adapting it here keeps the shape
 * mismatch in one place instead of leaking into the view.
 */
const getSignupRequestsList = async (params: {
  pagination?: ReactAdminPagination;
  filter?: ReactAdminFilter;
}) => {
  const config = RESOURCES.signupRequests;
  const page = params.pagination?.page ?? 1;
  const perPage = Math.min(params.pagination?.perPage ?? 25, SIGNUP_REQUESTS_MAX_PAGE);

  const query = new URLSearchParams({
    limit: String(perPage),
    offset: String((page - 1) * perPage),
  });
  const state = params.filter?.state;
  if (state) {
    query.set('state', String(state));
  }

  let response;
  try {
    response = await httpClient.get<ApiEnvelope<Record<string, any>[]> & { total?: number }>(
      `${config.endpoint}?${query.toString()}`
    );
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      return { data: [], total: 0 };
    }
    throw error;
  }

  const rows = Array.isArray(response.data?.data) ? response.data.data : [];
  return {
    data: rows.map((row) => ensureId(row, config)) as any,
    total: response.data?.total ?? rows.length,
  };
};

// ============================================
// DATA PROVIDER
// ============================================

/**
 * Endpoints without server-side list params: fetch everything, then apply
 * the React Admin filter (q = contains over string fields, others exact),
 * sort and pagination client-side.
 */
const getClientSideList = async (
  config: ResourceConfig,
  params: { pagination?: ReactAdminPagination; sort?: ReactAdminSort; filter?: ReactAdminFilter }
) => {
  let rows: Record<string, any>[] = [];
  try {
    const response = await httpClient.get<ApiEnvelope<ApiListData>>(config.endpoint);
    rows = parseListPayload(response.data).rows;
  } catch (error) {
    if (error instanceof HttpError && error.status === 404) {
      return { data: [], total: 0 };
    }
    throw error;
  }

  Object.entries(params.filter ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (key === 'q') {
      const q = String(value).toLowerCase();
      rows = rows.filter((row) =>
        Object.values(row).some(
          (field) => typeof field === 'string' && field.toLowerCase().includes(q)
        )
      );
    } else {
      rows = rows.filter((row) => String(row[key]) === String(value));
    }
  });

  const field = params.sort?.field;
  if (field && field !== 'id') {
    const desc = params.sort?.order === 'DESC' ? -1 : 1;
    rows.sort((a, b) => {
      const av = a[field] ?? '';
      const bv = b[field] ?? '';
      if (av < bv) return -1 * desc;
      if (av > bv) return 1 * desc;
      return 0;
    });
  }

  const total = rows.length;
  const page = params.pagination?.page ?? 1;
  const perPage = params.pagination?.perPage ?? 25;
  return {
    data: rows.slice((page - 1) * perPage, page * perPage).map((row) => ensureId(row, config)) as any,
    total,
  };
};

const baseDataProvider: DataProvider = {
  getList: async (resource, params) => {
    if (resource === 'callDetails') {
      return getCallDetailsList(params);
    }
    if (resource === 'signupRequests') {
      return getSignupRequestsList(params);
    }
    const config = getConfig(resource);
    if (config.clientSideList) {
      return getClientSideList(config, params);
    }
    const query = buildListQuery(config, params.pagination, params.sort, params.filter);
    let response;
    try {
      response = await httpClient.get<ApiEnvelope<ApiListData>>(
        `${config.endpoint}?${query}`
      );
    } catch (error) {
      // The API answers 404 to list requests with no matching rows
      if (error instanceof HttpError && error.status === 404) {
        return { data: [], total: 0 };
      }
      throw error;
    }
    const { rows, total } = parseListPayload(response.data);
    return {
      data: rows.map((row) => ensureId(row, config)) as any,
      total,
    };
  },

  getOne: async (resource, params) => {
    const config = getConfig(resource);

    if (config.getOneViaList) {
      const record = await fetchOneViaList(resource, params.id);
      return { data: ensureId(record, config) as any };
    }

    const response = await httpClient.get<ApiEnvelope<Record<string, any>>>(
      `${config.endpoint}/${params.id}`
    );
    const json = response.data;
    // Some endpoints (e.g. plans) wrap the single record in an array
    const payload = (json.data ?? json) as Record<string, any> | Record<string, any>[];
    const record = Array.isArray(payload) ? payload[0] : payload;
    if (!record) {
      throw new Error('No se ha encontrado el elemento solicitado');
    }
    return { data: ensureId(record, config) as any };
  },

  getMany: async (resource, params) => {
    const results = await Promise.allSettled(
      params.ids.map((id) => baseDataProvider.getOne(resource, { id }))
    );
    return {
      data: results
        .filter((r): r is PromiseFulfilledResult<{ data: any }> => r.status === 'fulfilled')
        .map((r) => r.value.data),
    };
  },

  getManyReference: async (resource, params) => {
    const { target, id, ...rest } = params;
    return baseDataProvider.getList(resource, {
      ...rest,
      filter: { ...params.filter, [target]: id },
    });
  },

  create: async (resource, params) => {
    const config = getConfig(resource);
    const body = buildWriteBody(config, params.data, config.createDefaults);
    const response = await httpClient.post<ApiEnvelope<Record<string, any>>>(
      config.createPath ?? config.endpoint,
      body
    );
    const json = response.data;
    if (json.status === 'error') {
      throw new Error(json.message || 'No se ha podido crear el elemento');
    }
    const record = (json.data ?? params.data) as Record<string, any>;
    return { data: ensureId(record, config) as any };
  },

  update: async (resource, params) => {
    const config = getConfig(resource);
    const previous = (params.previousData ?? {}) as Record<string, any>;
    const changed: Record<string, any> = {};
    Object.entries(params.data).forEach(([key, value]) => {
      if (JSON.stringify(value) !== JSON.stringify(previous[key])) {
        changed[key] = value;
      }
    });
    const meaningful = Object.keys(changed).filter((key) => !READONLY_FIELDS.has(key));
    if (meaningful.length === 0) {
      // Nothing to persist; some endpoints answer an error to no-op updates
      return { data: ensureId({ ...previous, ...params.data }, config) as any };
    }
    (config.alwaysSendOnUpdate ?? []).forEach((key) => {
      if (!(key in changed) && previous[key] !== undefined) {
        changed[key] = previous[key];
      }
    });
    const body = buildWriteBody(config, changed);
    const response = await httpClient.patch<ApiEnvelope<Record<string, any>>>(
      `${config.endpoint}/${params.id}`,
      body
    );
    const json = response.data;
    if (json.status === 'error') {
      throw new Error(json.message || 'No se ha podido actualizar el elemento');
    }
    const record =
      json.data && typeof json.data === 'object' && !Array.isArray(json.data)
        ? json.data
        : { ...params.previousData, ...params.data };
    return { data: ensureId(record as Record<string, any>, config) as any };
  },

  updateMany: async (resource, params) => {
    await Promise.all(
      params.ids.map((id) =>
        baseDataProvider.update(resource, {
          id,
          data: params.data,
          previousData: { id } as RaRecord,
        })
      )
    );
    return { data: params.ids };
  },

  delete: async (resource, params) => {
    const config = getConfig(resource);
    const suffix = config.deleteQuery ? `?${config.deleteQuery(params.previousData)}` : '';
    const response = await httpClient.delete<ApiEnvelope>(
      `${config.endpoint}/${params.id}${suffix}`
    );
    if (response.data?.status === 'error') {
      throw new Error(response.data.message || 'No se ha podido eliminar el elemento');
    }
    return { data: (params.previousData ?? { id: params.id }) as any };
  },

  deleteMany: async (resource, params) => {
    for (const id of params.ids) {
      await baseDataProvider.delete(resource, { id });
    }
    return { data: params.ids };
  },
};

// ============================================
// CUSTOM METHODS (non-CRUD operations)
// ============================================

export interface CustomDataProvider extends DataProvider {
  /** Invite a user (new or existing) to an organization by email */
  inviteUser: (params: {
    full_name?: string;
    email: string;
    organization_id: number;
    type?: string;
    role_id?: number;
  }) => Promise<ApiEnvelope>;
  /** Detach a staff member from an organization */
  removeStaffOrganization: (params: {
    staffId: number;
    organizationId: number;
    roleId: number;
  }) => Promise<ApiEnvelope>;
}

const customMethods = {
  inviteUser: async (params: {
    full_name?: string;
    email: string;
    organization_id: number;
    type?: string;
    role_id?: number;
  }): Promise<ApiEnvelope> => {
    const response = await httpClient.post<ApiEnvelope>('/users/inviteUser', params);
    return response.data;
  },

  removeStaffOrganization: async (params: {
    staffId: number;
    organizationId: number;
    roleId: number;
  }): Promise<ApiEnvelope> => {
    const response = await httpClient.post<ApiEnvelope>(
      '/users/removeStaffOrganization',
      params
    );
    return response.data;
  },
};

export const dataProvider: CustomDataProvider = {
  ...baseDataProvider,
  ...customMethods,
};

export default dataProvider;
